import type { LabelValuePair } from 'Types/common'

import {
  beRecipientApiGetAllSubscriptionsEmailFromList,
  beRecipientApiGetAllSubscriptionsSmsFromList,
  beRecipientApiOptinEmailRecipientFromMultiLists,
  beRecipientApiOptinSmsRecipientFromMultiLists,
  beRecipientApiPendingEmailRecipientFromMultiLists,
} from 'Api/beRecipientApiTS'

import { isNullOrEmpty } from 'Helpers/strings'
import type {
  ModalSubscribeState,
  SearchResult,
} from 'Pages/RecipientsList/model'
import type {
  ExitFromAnyOptoutTypeQsParam,
  WebhookSourceQsParam,
} from 'Types/beRecipientsApi'

export const getRecipientSubscriptionLists = async (
  recipient: SearchResult,
): Promise<number[]> => {
  if (!isNullOrEmpty(recipient.Email) && !isNullOrEmpty(recipient.Mobile)) {
    const [emailChannel, smsChannel] = await Promise.all([
      beRecipientApiGetAllSubscriptionsEmailFromList(recipient.Id),
      beRecipientApiGetAllSubscriptionsSmsFromList(recipient.Id),
    ])

    const emailSubscription: number[] = emailChannel
      .filter((c) => c.Status === 0) //  keep lists where not subscribed
      .map((c) => c.IdList)

    const smsSubscription: number[] = smsChannel
      .filter((c) => c.Status === 0) //  keep lists where not subscribed
      .map((c) => c.IdList)

    //  subscribed on both channels
    return emailSubscription.filter((n) => smsSubscription.includes(n))
  } else if (!isNullOrEmpty(recipient.Email)) {
    const emailChannel = await beRecipientApiGetAllSubscriptionsEmailFromList(
      recipient.Id,
    )

    const emailSubscription: number[] = emailChannel
      .filter((c) => c.Status === 0) //  keep lists where not subscribed
      .map((c) => c.IdList)

    return emailSubscription
  } else {
    const smsChannel = await beRecipientApiGetAllSubscriptionsSmsFromList(
      recipient.Id,
    )

    const smsSubscription: number[] = smsChannel
      .filter((c) => c.Status === 0) //  keep lists where not subscribed
      .map((c) => c.IdList)

    return smsSubscription
  }
}

export const subscribeMultilist = async ({
  idRecipient,
  lists,
  allLists,
  channelEmail,
  channelSMS,
  sendConfirmation,
  idConfirmationMessage,
  forceSubscription,
  availLists,
}: Pick<
  ModalSubscribeState,
  | 'idRecipient'
  | 'lists'
  | 'allLists'
  | 'channelEmail'
  | 'channelSMS'
  | 'sendConfirmation'
  | 'idConfirmationMessage'
  | 'forceSubscription'
> & {
  availLists: LabelValuePair[]
}): Promise<void> => {
  const IdLists = allLists ? availLists.map((o) => o.value) : lists

  const qsParams: WebhookSourceQsParam & ExitFromAnyOptoutTypeQsParam = {
    webhookSource: 'CONSOLE',
    exitFromAnyOptoutType: forceSubscription ? true : undefined,
  }

  const apiCalls = [
    channelEmail && sendConfirmation
      ? beRecipientApiPendingEmailRecipientFromMultiLists(
          idRecipient,
          qsParams,
          {
            IdLists,
            IdMsg: idConfirmationMessage,
          },
        )
      : undefined,
    channelEmail && !sendConfirmation
      ? beRecipientApiOptinEmailRecipientFromMultiLists(idRecipient, qsParams, {
          IdLists,
        })
      : undefined,
    channelSMS
      ? beRecipientApiOptinSmsRecipientFromMultiLists(idRecipient, qsParams, {
          IdLists,
        })
      : undefined,
  ].filter((apiCall) => apiCall !== undefined)

  await Promise.all(apiCalls)
}
